import { Divider, makeStyles, shorthands, tokens, Spinner } from '@fluentui/react-components';
import { FC, useRef, useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useNotification } from '../../Contexts/NotificationContext';
import { useUserContext } from '../../Contexts/UserContext';
import { useConfirm } from '../../Contexts/hooks';
import { ActionTypes, ConversationPatch, ConversationPage, GptTypes, IConversation, UserPreferences, Conversation, IUserPreferences, IGptConfiguration } from '../../EnterpriseGPTApi/EnterpriseGPTApiV1Client';
import { performEnterpriseGPTApiCall } from '../../Hooks/useMiddlewareApiCall';
import { useInfiniteConversations, getDefaultConversationPageSize } from '../../Hooks/useInfiniteConversations';
import { useConversationActions } from '../../Hooks/useConversationActions';
import { ChatRoute, ChatSpecificRoute, buildRoute } from '../../Routes';
import { clearConversationsAction, removeConversationAction, updateConversationAction, appendConversationsAction, addConversationAction, setConversationsCursorAction } from '../../store/actions/conversationActions';
import { useConversations } from '../../store/hooks';
import MenuConversations from './MenuConversations';
import MenuCommands from './MenuCommands';
import { Label } from '@fluentui/react-components';
import { ChevronDown, LayoutGrid, Globe, PlusCircle } from 'lucide-react';
import { SearchInput } from '../Common/SearchInput';
import SyChevron from './SyChevron';
import { useUserPreferences, useRootState, useGptConfigurations } from '../../store/hooks';
import Skeleton from 'react-loading-skeleton';
import { setUserPreferencesAction } from '../../store/actions/userPreferencesAction';
import AppMenuButton from './AppMenuButton';
import { AppConversation } from '../../store/models/conversation';
import { formatDateToDDMMYYYY, formatDateToDDMMYYYYHHMMSS } from '../../Utils/datetime';

const UseProjects = false;

const useStyles = makeStyles({
    bottom: {
        display: 'flex',
        flexDirection: 'column',
        rowGap: tokens.spacingVerticalM,
    },
    sectionLabel: {
        marginTop: 0,
        marginBottom: 0,
        display: 'block',
        textAlign: 'left',
        fontWeight: tokens.fontWeightSemibold,
        fontSize: tokens.fontSizeBase400,
        color: tokens.colorBrandForeground1,
        letterSpacing: '0.01em',
        paddingLeft: '1.5rem',
        paddingRight: '1.5rem',
        background: tokens.colorNeutralBackground1,
        borderRadius: tokens.borderRadiusMedium,
        lineHeight: 1.2,
    },
    sectionWrapper: {
        margin: 0,
        padding: 0,
        // Remove scrolling from this wrapper - parent container handles it
        display: 'flex',
        flexDirection: 'column',
        flex: '1 1 auto',
    },
    projectsList: {
        marginLeft: '3px',
    },
    autoHideScrollbar: {
        scrollbarWidth: 'thin',
        scrollbarColor: `${tokens.colorNeutralForeground3} ${tokens.colorNeutralBackground1}`,
        '&::-webkit-scrollbar': {
            width: '8px',
            background: tokens.colorNeutralBackground1,
        },
        '&::-webkit-scrollbar-thumb': {
            background: tokens.colorNeutralForeground3,
            borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
            background: tokens.colorNeutralForeground2,
        },
    },
});

interface AppMenuContentProps {
    onCloseMenuRequired?: () => void;
    onNewDraftChat: (gptConfiguration: IGptConfiguration) => void;
}
export const AppMenuContent: FC<AppMenuContentProps> = (props: AppMenuContentProps) => {
    const { onCloseMenuRequired, onNewDraftChat } = props;
    const classes = useStyles();
    const { userProfile } = useUserContext();
    const { state } = useRootState();
    const appLoading = state.appLoading ?? false;
    const conversationsLoading = state.conversationsLoading ?? false;
    const loading = appLoading || conversationsLoading;
    const { t } = useTranslation();
    const { activeChat, chats, conversationsCursor, dispatch } = useConversations();
    const { gptConfigurations } = useGptConfigurations();
    const addedGpts = gptConfigurations || [];
    const gpt = addedGpts.find(g => g.isDefault) || addedGpts[0];
    const { handleRemoveConversation, handleConversationTitleChange, handlePinToggle } = useConversationActions();
    const navigate = useNavigate();
    const { dispatchNotification } = useNotification();
    const confirm = useConfirm();
    const [activeSearchText, setActiveSearchText] = useState(''); // Only state for actual search
    const [searchResults, setSearchResults] = useState<AppConversation[]>([]); // Store server search results
    const [isSearching, setIsSearching] = useState(false); // Loading state for search
    const [isSearchFocused, setIsSearchFocused] = useState(false);
    // Local controlled input value to manage clear button and live state
    const [localSearchInput, setLocalSearchInput] = useState('');
    const activeSearchTextRef = useRef(activeSearchText);
    const searchContainerRef = useRef<HTMLDivElement>(null);
    const conversationsScrollContainerRef = useRef<HTMLDivElement>(null); // Ref to conversations scroll container
    
    // Sync search results with chats store - remove deleted conversations
    useEffect(() => {
        if (searchResults.length > 0) {
            const chatIds = new Set(chats.map(c => c.id));
            setSearchResults(prevResults => prevResults.filter(c => chatIds.has(c.id)));
        }
    }, [chats, searchResults.length]);

    // Use infinite query for loading more conversations
    // Disable automatic fetching to prevent double-load on startup
    // (Router.tsx handles initial load, this hook only handles "Load More")
    // Use the cursor from Redux to continue pagination from where Router.tsx left off
    const {
        data: infiniteData,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useInfiniteConversations({
        projectId: null,
        limit: getDefaultConversationPageSize(),
        enabled: false, // Never auto-fetch, only fetch when user clicks "Load More"
        initialCursor: conversationsCursor, // Start from where Router.tsx left off
    });

    // Determine if there might be more conversations to load
    // Show "Load More" if:
    // 1. React Query has determined there's a next page (hasNextPage === true)
    // 2. OR we have a cursor from initial load (before any infinite query pages are fetched)
    // We check if infiniteData has pages - if not, the query hasn't run yet, so use cursor
    const hasMoreConversations = hasNextPage === true || (!infiniteData?.pages?.length && conversationsCursor !== undefined);
    
    // Track the number of pages we've already processed to avoid re-processing
    const processedPagesRef = useRef(0);

    // Sync infinite query data with Redux store
    useEffect(() => {
        if (infiniteData?.pages && infiniteData.pages.length > processedPagesRef.current) {
            // Only process new pages that haven't been processed yet
            const newPages = infiniteData.pages.slice(processedPagesRef.current);
            const newConversations: AppConversation[] = [];
            
            newPages.forEach((page: ConversationPage) => {
                if (page.conversations) {
                    page.conversations.forEach(conv => {
                        newConversations.push({
                            ...conv,
                            active: false,
                            draft: false,
                            prompts: conv.prompts?.map((p) => ({ ...p, aborted: p.answer === null }))
                        });
                    });
                }
            });
            
            if (newConversations.length > 0) {
                dispatch(appendConversationsAction(newConversations));
            }
            
            // Update the cursor from the last page loaded
            const lastPage = infiniteData.pages[infiniteData.pages.length - 1];
            if (lastPage) {
                dispatch(setConversationsCursorAction(lastPage.nextCursor ?? undefined));
            }
            
            // Update the count of processed pages
            processedPagesRef.current = infiniteData.pages.length;
        }
    }, [infiniteData, dispatch]);

    useEffect(() => {
        activeSearchTextRef.current = activeSearchText;
    }, [activeSearchText]);


    // Filter conversations: match search text in title OR any prompt content,
    // then sort by number of found term occurrences (descending), so conversations with more matches appear first
    const filteredChats = useMemo(() => {
        if (!activeSearchText.trim()) {
            // No sorting - maintain pagination order from server
            return chats.filter((c) => !c.draft);
        }
        // Use server-returned search results instead of client-side filtering
        return searchResults.filter((c) => !c.draft);
    }, [chats, activeSearchText, searchResults]);


    // Partition filteredChats into two groups for search: found in title, found in prompts
    const [foundInTitle, foundInPrompts] = useMemo(() => {
        if (!activeSearchText.trim()) return [[], []];
        
        const searchLower = activeSearchText.toLowerCase();
        const inTitle: AppConversation[] = [];
        const inPrompts: AppConversation[] = [];
        
        searchResults.forEach((conversation) => {
            // Check if search term is in the title
            const titleMatch = conversation.title?.toLowerCase().includes(searchLower);
            
            // Check if search term is in any prompt
            const promptMatch = conversation.prompts?.some(prompt => 
                prompt.query?.toLowerCase().includes(searchLower) || 
                prompt.answer?.toLowerCase().includes(searchLower)
            );
            
            // Add to appropriate section (title takes priority)
            if (titleMatch) {
                inTitle.push(conversation);
            } else if (promptMatch) {
                inPrompts.push(conversation);
            }
        });
        
        return [inTitle, inPrompts];
    }, [searchResults, activeSearchText]);



    const resolveFallbackConversation = (items: AppConversation[]): AppConversation | undefined => {
        if (!items.length) {
            return undefined;
        }

        // Filter out draft conversations from fallback resolution
        const nonDraftItems = items.filter(c => !c.draft);
        
        if (!nonDraftItems.length) {
            return undefined;
        }

        return (
            nonDraftItems.find((c) => c.gpt?.type === GptTypes.SyGpt && (!c.prompts || c.prompts.length === 0)) ??
            nonDraftItems.find((c) => !c.prompts || c.prompts.length === 0) ??
            nonDraftItems.find((c) => c.gpt?.type === GptTypes.SyGpt) ??
            nonDraftItems[0]
        );
    };

    const handleConversationSelected = async (id: string) => {

        // Find the conversation in the store, or in search results if we're searching
        let conversation = chats.find(c => c.id === id);
        
        // Determine if we need to load full conversation data
        let needsFullLoad = false;
        
        // If not found in chats and we have search results, check there
        if (!conversation && activeSearchText.trim()) {
            const searchResult = searchResults.find(c => c.id === id);
            if (searchResult) {
                conversation = searchResult;
                needsFullLoad = searchResult.fullyLoaded !== true;
            }
        } else if (conversation) {
            // Check if existing conversation needs full load
            // A conversation needs full load if:
            // 1. It's explicitly marked as not fully loaded
            // 2. OR it doesn't have prompts loaded (undefined or null)
            needsFullLoad = conversation.fullyLoaded !== true || !conversation.prompts;
        }
        
        // If conversation not found at all, return early
        if (!conversation) {
            console.error('Conversation not found:', id);
            return;
        }
        
        // Load full conversation data from server if needed
        if (needsFullLoad) {
            const fullConversationData = await performEnterpriseGPTApiCall(async (client, signal) => {
                const fullConversation = await client.getConversationById(id, signal);
                if (!fullConversation) {
                    throw new Error('Conversation not found or empty response');
                }
                
                const appConversation: AppConversation = {
                    ...fullConversation,
                    active: false,
                    draft: false,
                    prompts: fullConversation.prompts?.map((p) => ({ ...p, aborted: p.answer === null })),
                    fullyLoaded: true // Mark as fully loaded
                };
                
                return appConversation;
            });
            
            if (!fullConversationData) {
                console.error('Failed to load full conversation:', id);
                dispatchNotification('error.api.get.fullConversation', 'error');
                return; // Don't navigate if loading failed
            }
            
            // Update the store with the fully-loaded conversation
            // Use addConversationAction if conversation wasn't in chats originally, otherwise use updateConversationAction
            const existsInChats = chats.find(c => c.id === id);
            if (existsInChats) {
                dispatch(updateConversationAction({ 
                    id: fullConversationData.id, 
                    patch: fullConversationData
                }));
            } else {
                dispatch(addConversationAction(fullConversationData));
            }
            
            conversation = fullConversationData;
        }
        
        // Log conversation details for debugging
        if (conversation) {
            const lastUpdate = formatDateToDDMMYYYYHHMMSS(conversation?.lastUpdateOn);
            //console.log('Selecting conversation:', conversation?.title, 'Last Changed on:', lastUpdate, 'Prompts:', conversation?.prompts?.length);
        }
        
        // Navigate to the conversation
        navigate(buildRoute(ChatSpecificRoute, { id }));
        // Close menu only in mobile mode when a conversation is selected
        if (onCloseMenuRequired) {
            onCloseMenuRequired();
        }
    };

    const { userPreferences, dispatch: userPreferencesDispatch } = useUserPreferences();

    // Toggle for showing/hiding the Conversations section in the menu
    const [conversationsExpanded, setConversationsExpanded] = useState<boolean>(true);


    // Toggle for showing/hiding the Projects section in the menu
    const [projectsExpanded, setProjectsExpanded] = useState<boolean>(true);


    const handleToggleConversations = useCallback((e?: React.KeyboardEvent | React.MouseEvent) => {
        // If keyboard event, only toggle on Enter or Space
        if (e && 'key' in (e as React.KeyboardEvent)) {
            const key = (e as React.KeyboardEvent).key;
            if (key !== 'Enter' && key !== ' ') return;
            e.preventDefault();
        }
        setConversationsExpanded((s) => !s);
    }, []);

    const handleToggleProjects = useCallback((e?: React.KeyboardEvent | React.MouseEvent) => {
        // If keyboard event, only toggle on Enter or Space
        if (e && 'key' in (e as React.KeyboardEvent)) {
            const key = (e as React.KeyboardEvent).key;
            if (key !== 'Enter' && key !== ' ') return;
            e.preventDefault();
        }
        setProjectsExpanded((s) => !s);
    }, []);

    const handleDisenroll = () => {
        confirm({
            message: t('menu.persona.disenrollInfo'),
            show: true,
            title: t('menu.persona.disenrollConfirm'),
            cancelText: t('No'),
            confirmText: t('Yes'),
        }).then(() => {
            performEnterpriseGPTApiCall(async (client, cancelToken) => {
                try {
                    await client.unenroll(cancelToken);
                    document.location.reload();
                } catch {
                    dispatchNotification('error.api.delete.enrollment', 'error');
                }
            });
        });
    };

      // --- Server-side search behavior ---
    // Trigger search on Enter or search button click - call server API
    const handleSearch = useCallback(() => {
        const searchText = localSearchInput.trim();
        if (!searchText) {
            setActiveSearchText('');
            setSearchResults([]);
            setIsSearching(false);
            return;
        }
        
        setIsSearching(true);
        performEnterpriseGPTApiCall(async (client, signal) => {
            try {
                const results = await client.getConversationsFiltered(searchText, false, signal);
                const appConversations: AppConversation[] = (results || []).map(conv => ({
                    ...conv,
                    active: false,
                    draft: false,
                    fullyLoaded: false, // Mark as not fully loaded since we used fullLoad: false
                    prompts: conv.prompts?.map((p) => ({ ...p, aborted: p.answer === null }))
                }));
                setSearchResults(appConversations);
                setActiveSearchText(searchText);
            } catch (error) {
                console.error('Search failed:', error);
                dispatchNotification('error.api.search.conversations', 'error');
                setSearchResults([]);
            } finally {
                setIsSearching(false);
            }
        });
    }, [localSearchInput, dispatchNotification]);

    // Detect input changes to keep local state and clear search when emptied
    const handleInputChange = useCallback((_: unknown, data: { value: string }) => {
        setLocalSearchInput(data.value);
        if (data.value === '') {
            setActiveSearchText('');
        }
    }, []);

    const handleClearSearch = useCallback(() => {
        setLocalSearchInput('');
        setActiveSearchText('');
        setSearchResults([]);
        setIsSearching(false);
    }, []);

    // Global Escape key handler to clear search
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                if (activeSearchTextRef.current.trim()) {
                    handleClearSearch();
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleClearSearch]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', overflow: 'hidden', position: 'relative' }}>
            <style>
                {`
                    @keyframes spin {
                        from { transform: rotate(0deg); }
                        to { transform: rotate(360deg); }
                    }
                `}
            </style>
            
            {/* Search box for conversations - classic search */}
            {!loading && (<SearchInput
                value={localSearchInput}
                onChange={handleInputChange}                
                onSearch={handleSearch}
                onClear={handleClearSearch}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setIsSearchFocused(false)}
                placeholder="Search …"
                ariaLabel={t('menu.search.ariaLabel', 'Search conversations')}
                isSearching={isSearching}
            />
            )}


            {/* Conversations & projects container - takes up remaining space */}
            <div
                ref={conversationsScrollContainerRef}
                style={{ flex: '1 1 auto', overflowY: 'auto', overflowX: 'hidden', minHeight: 0 }}
                className={classes.autoHideScrollbar}
            >
                
                {!activeSearchText.trim() && !loading && <MenuCommands onNewDraftChat={onNewDraftChat} showOnlyToggleMenuCommand={false} />}
                
                {activeSearchText.trim() && (
                    <div className={classes.sectionWrapper}>

                        <Label className={classes.sectionLabel}>
                            In the conversation title ({foundInTitle.length})
                        </Label>

                        {!loading && (
                            <MenuConversations
                                items={foundInTitle}
                                selectedItem={activeChat}
                                onRemoveConversation={handleRemoveConversation}
                                onConversationTitleChange={handleConversationTitleChange}
                                onConversationSelected={handleConversationSelected}
                                hidePinned={true}
                                onPinToggle={handlePinToggle}
                            />
                        )}

                        <Label className={classes.sectionLabel} style={{ marginTop: 0, marginBottom: 0 }}>
                            In the conversation prompts ({foundInPrompts.length})
                        </Label>

                        {!loading && (
                            <MenuConversations
                                items={foundInPrompts}
                                selectedItem={activeChat}
                                onRemoveConversation={handleRemoveConversation}
                                onConversationTitleChange={handleConversationTitleChange}
                                onConversationSelected={handleConversationSelected}
                                hidePinned={true}
                                onPinToggle={handlePinToggle}
                            />
                        )}

                        {foundInTitle.length === 0 && foundInPrompts.length === 0 && (
                            <Label size="small" style={{ marginLeft: 16, marginTop: 16, marginBottom: 4, display: 'block' }}>
                                No results found.
                            </Label>
                        )}
                    </div>
                )}

                {!activeSearchText.trim() && (
                    <div>

                        {UseProjects && (
                            <>
                                {/* Projects list */}

                                <div className="sectionLabelWithChevron" >
                                    {!loading && (
                                        <SyChevron
                                            expanded={projectsExpanded}
                                            onToggle={handleToggleProjects}
                                            ariaControls="menu-projects-list"
                                            title={t('menu.projects.toggle', 'Toggle projects')}
                                            iconClassName={'sy-chevron-base'}
                                        />
                                    )}

                                    {!loading && (
                                        <Label className="menuSectionLabel" size='small' style={{ marginLeft: 3 }}>
                                            {t('menu.projects.title', 'Projects')}
                                        </Label>
                                    )}
                                </div>
                                {projectsExpanded && (
                                    <div id="menu-projects-list" className={classes.projectsList}>
                                        {loading ? (
                                            (() => {
                                                return (
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 6 }}>
                                                        <Skeleton className="skeleton-253x83 w-80p" />
                                                    </div>
                                                );
                                            })()
                                        ) : (
                                            <AppMenuButton
                                                title={'menu.projects.newProject'}
                                                label={'menu.projects.newProject'}
                                                color={'var(--color-primary-500)'}
                                                icon={<PlusCircle />}
                                                changeBackgroundOnHover={false}
                                                onClick={() => {
                                                }}
                                            />
                                        )}

                                    </div>
                                )}

                               <Divider className="menuSmallDividerIn" />

                            </>
                        )}

                        {/* Chats list */}

                        <div className="sectionLabelWithChevron">
                            {!loading && (
                                <SyChevron
                                    expanded={conversationsExpanded}
                                    onToggle={handleToggleConversations}
                                    ariaControls="menu-conversations-list"
                                    title={t('menu.conversations.toggle', 'Toggle conversations')}
                                    iconClassName={'sy-chevron-base'}
                                />
                            )}

                            {!loading && (
                                <Label className="menuSectionLabel" size='small' style={{ marginLeft: 3 }}>
                                    {t('menu.conversations.historyTitle', 'Chats')}
                                </Label>
                            )}
                        </div>

                        {!loading && gpt && (
                            <div style={{ marginLeft: '3px', marginTop: '4px' }}>
                                <AppMenuButton
                                    title={'New chat'}
                                    label={'New chat'}
                                    color={'var(--color-primary-500)'}
                                    icon={<PlusCircle />}
                                    changeBackgroundOnHover={false}
                                    onClick={() => {
                                        onNewDraftChat(gpt);
                                    }}
                                />
                            </div>
                        )}

                                {conversationsExpanded && (
                                    <div id="menu-conversations-list" style={{ paddingLeft: '3px' }}>
                                        {loading ? (
                                            (() => {
                                                return (
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                            <Skeleton className="skeleton-253x32" />
                                                            <Skeleton className="skeleton-253x32" />
                                                            <Skeleton className="skeleton-253x32" />
                                                            <Skeleton className="skeleton-253x32" />
                                                            <Skeleton className="skeleton-253x32" />
                                                            <Skeleton className="skeleton-253x32" />
                                                        </div>
                                                    </div>
                                                );
                                            })()
                                        ) : (
                                            <MenuConversations
                                                items={chats.filter((c) => !c.draft)}
                                                selectedItem={activeChat}
                                                onRemoveConversation={handleRemoveConversation}
                                                onConversationTitleChange={handleConversationTitleChange}
                                                onConversationSelected={handleConversationSelected}
                                                onPinToggle={handlePinToggle}
                                                onLoadMore={fetchNextPage}
                                                hasMore={hasMoreConversations}
                                                isLoadingMore={isFetchingNextPage}
                                                scrollContainerRef={conversationsScrollContainerRef}
                                            />
                                        )
                                        }
                                    </div>
                                )}
                    </div>
                )}
            </div>


        </div>
    );
};
